import React, { useCallback } from 'react'
import config from 'cityads-v3/config';
import { useTranslation } from 'react-i18next'
import classes from './Subaccounts.scss';

import AddIcon from '@mui/icons-material/AddOutlined'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import TextField, { TextFieldProps } from 'cityads-v3/components/TextField/TextField'
import Tooltip from 'cityads-v3/components/Tooltip/Tooltip'


type SubaccountsProps = Omit<TextFieldProps,
  'placeholder' | 'onChange' | 'className' | 'style' | 'value' | 'ref'
> & {
  className?: string,
  labelAll?: boolean,
  maxLength?: number,
  maxSubaccounts?: number,
  onAdd?: (length: number) => void,
  onChange: (newValue: string[]) => void,
  placeholder?: (num: number) => string,
  renderMinMaxLength?: boolean,
  style?: React.CSSProperties,
  textFieldClasses?: TextFieldProps['classes'],
  tooltip?: string,
  tooltipIsLight?: boolean,
  value: string[],
  wrap?: boolean,
}

export default function Subaccounts({
  className,
  style,
  labelAll,
  margin,
  maxLength = config.SUBACCOUNT_MAX_LENGTH,
  maxSubaccounts = config.SUBACCOUNT_MAX_SUBACCOUNTS,
  onAdd,
  onBlur,
  onChange,
  placeholder = num => 'sa' + (num ? num + 1 : ''),
  textFieldClasses,
  tooltip,
  tooltipIsLight,
  value = [''],
  wrap = true,
  ...textFieldProps
}: SubaccountsProps): JSX.Element {
  const { t } = useTranslation(['Offers'])

  const handleChange = useCallback((index: number) => (evt: React.ChangeEvent<HTMLInputElement>) => {
    const setValue = [...value]
    setValue[index] = evt.target.value
    onChange(setValue)
  }, [value, onChange])

  const handleAddSA = useCallback(() => {
    if (value.length < maxSubaccounts) {
      const newValue = [...value, '']
      onChange(newValue)
      onAdd?.(newValue.length)
    }
  }, [value, maxSubaccounts, onChange, onAdd])

  const values = value.map((item, num) => (
    <TextField
      {...textFieldProps}
      classes={textFieldClasses}
      key={num}
      label={num && !labelAll ? '' : (
        <span>
          {t('exports.addSA')} {labelAll && ` #${num + 1}`}
          {tooltip && (
            <Tooltip
              isLight={tooltipIsLight}
              title={tooltip}
            >
              <InfoOutlinedIcon classes={{ root: classes.tooltip__icon }} />
            </Tooltip>
          )}
        </span>
      )}
      InputLabelProps={{ shrink: true }}
      fullWidth={true}
      placeholder={placeholder(num)}
      value={item}
      onBlur={onBlur}
      onChange={handleChange(num)}
      maxLength={maxLength}
      margin={margin}
      inputProps={{
        "data-subaccount": num + 1,
      }}
      InputProps={{
        endAdornment: (num + 1 === value.length && value.length < maxSubaccounts) ? (
          <InputAdornment position="end">
            <IconButton
              disableRipple
              disableFocusRipple
              edge="end"
              onClick={handleAddSA}
              size="large"
            >
              <AddIcon />
            </IconButton>
          </InputAdornment>
        ) : null
      }}
    />
  ))

  return wrap ? (
    <div className={className} style={style}>
      {values}
    </div>
  ) : <>{values}</>
}
